import { Component, Input, Output, OnInit, OnDestroy, DestroyRef, inject, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalConfig, NgbToastModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { PolicyService } from '@services/policy.service';
import { CoreModule } from 'app/modules/core.module';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormBuilder, Validators } from '@angular/forms';
import { InsuredService } from '@services/insured.service';
import { GroupOfPoliceService } from '@services/groupOfPolicies.service';
import Swal from 'sweetalert2';
import { EntityService } from '@services/entity.service';
import { PositionService } from '@services/position.service';
import { ContractInsuredService } from '@services/contracts.service';
import { apiResponse, apiLog, apiError, apiInfo } from 'app/shared/api-response';

@Component({
  selector: 'app-policy-information',
  standalone: true,
  imports: [CoreModule, NgSelectComponent, NgbTooltipModule, NgbToastModule],
  providers: [PolicyService, InsuredService, EntityService, GroupOfPoliceService, PositionService, ContractInsuredService],
  templateUrl: './policy-information.component.html',
  styleUrls: ['./policy-information.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PolicyInformationComponent {
  @Input() insuredId: string
  @Input() hideNavigation: boolean = false
  @Input() existingCoverageIds: string[] = []
  @Input() existingEntityIds: string[] = []
  @Output() cancel = new EventEmitter<void>()
  @Output() back = new EventEmitter<void>()
  @Output() savePolicyInformation = new EventEmitter<any>()


  private _insuredService: InsuredService = inject(InsuredService)
  private _entityService = inject(EntityService)
  private _policyService = inject(PolicyService)
  private _groupPolicyService = inject(GroupOfPoliceService)
  private _positionService = inject(PositionService)
  private _contractInsuredService = inject(ContractInsuredService)
  private fb: FormBuilder = inject(FormBuilder)
  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)
  protected showSpinner = true
  protected listInsuredTypes: any[] = []
  protected listEntities: any[] = []
  protected listPolicies: any[] = []
  protected listPoliciesTemp: any[] = []
  protected listGroups = []
  protected listPositions = []

  protected holderForm: any
  protected minDate = (() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })()

  protected maxDate = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 30)
    return d.toISOString().split('T')[0]
  })()

  protected findHolderData = false
  protected showMissionaryForm = false
  protected typeBoard = ["Junta Directiva", "Comisión Interna"]
  private holderInformation: any
  protected selectedRelationship: any


  public transferForm: any
  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 5000,
    timerProgressBar: true
  })




  ngOnInit() {


    this.getEntities()
    this.getInsuredRelationship()

    this.transferForm = this.fb.group({
      relationshipId: [null, Validators.required],
      holderId: [null, Validators.required],
      coverageDestinyId: [null, Validators.required],
      holder: this.fb.group({
        rut: [null],
        names: [null],
        surname: [null],
        lastname: [null],
        position: [null]
      }),
      entityId: [null, Validators.required],
      policyId: [null, Validators.required],
      groupId: [null, Validators.required],
      jobTitleId: [null, Validators.required],
      capital: [null, Validators.required],
      dateStart: [null, Validators.required],
      voteType: [null],
      voteNumber: [null],
      voteDate: [null],
      voteDetail: [null],
    })


    this.showSpinner = false
  }


  getEntities() {
    this._entityService.getAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de entidades')
      this.listEntities = res
      if (this.existingEntityIds.length) {
        this.listEntities = this.listEntities.filter(e => this.existingEntityIds.includes(e._id))
      }
      this.cdr.markForCheck()
    })
  }

  getInsuredRelationship() {
    this._insuredService.getInsuredTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de tipos de asegurado')
      this.listInsuredTypes = res
      this.cdr.markForCheck()
    })
  }

  getInsuredByRut() {
    if (this.holderForm.value.rut != null) {
      this._insuredService.getInsuredByRut(this.holderForm.value.rut).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
        apiLog(res, 'Búsqueda de asegurado por RUT')
        const data = res[0]
        if (data) {
          this.transferForm.patchValue({ holderId: data._id })
        }
      })
    }
  }

  getHolderInformation() {
    this._insuredService.getInsuredByRut(this.transferForm.value.holder.rut).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Búsqueda de información del titular')
      const data = res[0]
      if (data) {
        this.transferForm.patchValue({ holder: { ...data, position: data.position?.name || data.position }, holderId: data._id })
        this.getPoliciesHolder()
      } else {
        this.transferForm.get('holder').reset()
        apiInfo('No se encontró el asegurado')
      }
    })
  }

  changeTypeInsured(data: any) {
    this.selectedRelationship = data
    this.transferForm.get('holder').reset()
    if (data.priority >= 1) {
      this.findHolderData = true
      this.transferForm.patchValue({ holderId: null })
      this.transferForm.get('holderId').setValidators([Validators.required])
    }
    else {
      this.findHolderData = false
      this.transferForm.patchValue({ holderId: this.insuredId || this.transferForm.value.insuredId })
      this.transferForm.get('holderId').setValidators([])
    }

    this.transferForm.get('holderId').updateValueAndValidity();
    this._applyPoliciesDependentsFilter()
  }

  private _applyPoliciesDependentsFilter(): void {
    if (!this.listPoliciesTemp.length) return
    const isDependent = this.selectedRelationship?.priority >= 1
    this.listPolicies = this.listPoliciesTemp.filter(p => (!isDependent || p.coverage?.allowsDependents))
    if (this.holderInformation) {
      this.listPolicies = this.listPolicies.filter(p => p.policyCode == this.holderInformation.policy.policyCode)
    }
  }

  getPoliciesDestiny() {

    this._policyService.getByCampus(this.transferForm.value.entityId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de pólizas disponibles')
      this.listPoliciesTemp = res
      if (this.existingCoverageIds.length) {
        this.listPoliciesTemp = this.listPoliciesTemp.filter(p => !this.existingCoverageIds.includes(p.coverage?._id))
      }
      this._applyPoliciesDependentsFilter()
      this.cdr.markForCheck()
    })
  }

  getPoliciesHolder() {
    this._contractInsuredService.getPoliciesAvailables(this.transferForm.value.holderId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de pólizas del titular')
      this.holderInformation = res[0]
      this.listEntities = this.listEntities.filter(e => e._id == res[0].policy.entityId)
      this._applyPoliciesDependentsFilter()
      this.cdr.markForCheck()
    })
  }

  getGroupOfPolicy(item: any) {
    this.transferForm.patchValue({ policyId: item._id, coverageDestinyId: item.coverageId})
    this._groupPolicyService.getByPolicy(this.transferForm.value.policyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de grupos')
      this.listGroups = res
      this.setMissionaryPolicies()
      this.cdr.markForCheck()
    })
  }

  getPositions() {
    this._positionService.getByGroup(this.transferForm.value.groupId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de cargos')
      this.listPositions = res
      this.cdr.markForCheck()
    })
  }

  setCapital() {
    this._positionService.setCapital(this.transferForm.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de capital')
      this.transferForm.patchValue({ capital: res.capital })
    })
  }

  setMissionaryPolicies() {
    const policy: any = this.listPolicies.find((p: any) => p._id == this.transferForm.value.policyId)
    if (policy.coverage.missionary) {
      this.showMissionaryForm = true
      this.transferForm.get('voteType').setValidators([Validators.required])
      this.transferForm.get('voteNumber').setValidators([Validators.required])
      this.transferForm.get('voteDate').setValidators([Validators.required])
      this.transferForm.get('voteDetail').setValidators([Validators.required])
    } else {
      this.showMissionaryForm = false
      this.transferForm.get('voteType').setValidators([])
      this.transferForm.get('voteNumber').setValidators([])
      this.transferForm.get('voteDate').setValidators([])
      this.transferForm.get('voteDetail').setValidators([])
    }

    this.transferForm.get('voteType').updateValueAndValidity();
    this.transferForm.get('voteNumber').updateValueAndValidity();
    this.transferForm.get('voteDate').updateValueAndValidity();
    this.transferForm.get('voteDetail').updateValueAndValidity();
  }

  save() {
    if (!this.transferForm.valid) {
      this.transferForm.markAllAsTouched()
      apiInfo('Confirmar los datos ingresados que esten correctos')
      return
    }
    this.saveChecked()
  }

  private saveChecked() {
    if (this.selectedRelationship?.priority === 0 && this.insuredId) {
      this.verifyTitularPriority()
      return
    }
    this.emitPolicyData()
  }

  private emitPolicyData() {
    const data = this.insuredId ? { ...this.transferForm.value, insuredId: this.insuredId } : this.transferForm.value
    this.savePolicyInformation.emit(data)
  }

  private verifyTitularPriority() {
    this._contractInsuredService.getPoliciesAvailables(this.insuredId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        apiLog(res, 'Verificación de pólizas del titular')
        if (res?.length) {
          this.confirmTitularPriority()
        } else {
          this.emitPolicyData()
        }
      },
      error: (err) => apiError(err, 'Error al verificar titular')
    })
  }

  private confirmTitularPriority() {
    Swal.fire({
      title: "El titular ya posee pólizas activas",
      text: "La nueva póliza tomará prioridad sobre la actual. ¿Desea continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, continuar",
      cancelButtonText: "Cancelar"
    }).then(result => {
      if (result.isConfirmed) this.emitPolicyData()
    })
  }
}
